import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import { useEffect, useState, useRef } from 'react';
import type { CallProvider } from 'src/hooks/phones/types';
import { formatDuration } from 'src/modules/shared/utils/formatting/time.util';
import { motion } from 'framer-motion';
import { Typography } from '@material-tailwind/react';
import STATES from 'states/index';
import { usePhoneContext } from 'src/contexts/phone-context';
import { useGlobalPhoneContext } from 'src/contexts/global-phone-context';
import { SettingsButton } from '../../../atoms/phones/buttons/settings-button';
import { EndCallButton } from '../../../atoms/phones/buttons/end-call-button';
import { MuteButton } from '../../../atoms/phones/buttons/mute-button';
import { AcceptCallButton } from '../../../atoms/phones/buttons/accept-call-button';
import { useContactsByPhone } from 'src/modules/communications/hooks/api/contacts-by-address';
import { ContactInfo } from './contact-info';
import { useNavigate } from '@tanstack/react-router';
import { Route as CommunicationsRoute } from 'src/routes/communications';
import { communicationsSearchSchema } from 'src/modules/shared/schemas/communications-search.schema';

// type Position = PanInfo['point'];

interface FloatingPhoneProps {
  callProvider: CallProvider;
}

const phoneWrapperClasses = [
  'absolute top-1/2 left-1/2',
  '-translate-x-1/2 -translate-y-1/2',
  'pointer-events-auto',
].join(' ');
const phoneContentClasses = [
  'bg-white rounded-lg shadow-lg',
  'p-6 flex flex-col items-center',
  'gap-6 justify-between',
  'min-w-[240px] min-h-[200px]',
].join(' ');
const callControlsClasses = 'flex items-center gap-4';

export const FloatingPhone: React.FC<FloatingPhoneProps> = ({
  callProvider: {
    callActions,
    loading: callProviderLoading,
    error: callProviderError,
    callerNumber,
    onCallNumber,
  },
}) => {
  const [duration, setDuration] = useState(0);
  const { phoneState, isMuted, setIsMuted } = usePhoneContext();
  const { isGlobalPhoneVisible, isGlobalPhoneEnabled, setGlobalPhoneVisible } =
    useGlobalPhoneContext();
  // const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const constraintsRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  // const dragControls = useDragControls();
  const navigate = useNavigate();

  const currentNumber =
    phoneState === STATES.PHONE.INCOMING ? callerNumber : onCallNumber;

  const { contacts, loading: contactsLoading } = useContactsByPhone({
    phoneNumber: nonEmptyString(currentNumber) ?? null,
    enabled: !!currentNumber,
  });

  useEffect(() => {
    if (phoneState === STATES.PHONE.INCOMING) {
      setGlobalPhoneVisible(true);
    }
  }, [phoneState, isGlobalPhoneEnabled, setGlobalPhoneVisible]);

  useEffect(() => {
    if (phoneState !== STATES.PHONE.ESTABLISHED) return;

    const timer = setInterval(() => {
      setDuration((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [phoneState]);

  useEffect(() => {
    if (phoneState === STATES.PHONE.ESTABLISHED) return;

    setDuration(0);
  }, [phoneState]);

  if (!isGlobalPhoneEnabled || !isGlobalPhoneVisible) return null;

  if (
    phoneState === STATES.PHONE.READY ||
    phoneState === STATES.PHONE.REGISTERING
  ) {
    return null;
  }

  if (callProviderLoading || callProviderError) return null;

  const handleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    callActions.muteCall(newMutedState);
  };

  // const handleDragEnd = (
  //   _: MouseEvent | TouchEvent | PointerEvent,
  //   info: PanInfo
  // ) => {
  //   setPosition({
  //     x: position.x + Math.round(info.offset.x),
  //     y: position.y + Math.round(info.offset.y),
  //   });
  // };

  const handleOpenProfile = (contactId: number) => {
    void navigate({
      to: CommunicationsRoute.to,
      search: (prev) =>
        communicationsSearchSchema.parse({
          ...prev,
          contactId,
        }),
    });
  };

  const renderCallControls = () => {
    if (phoneState === STATES.PHONE.INCOMING) {
      return (
        <div className={callControlsClasses}>
          <AcceptCallButton onClick={callActions.acceptCall} />
          <EndCallButton onClick={callActions.rejectCall} />
        </div>
      );
    }

    return (
      <div className={callControlsClasses}>
        <MuteButton onClick={handleMute} isMuted={isMuted} />
        <EndCallButton onClick={callActions.endCall} />
        <SettingsButton
          disabled
          onClick={() => {
            /* Disabled while a call is active. */
          }}
        />
      </div>
    );
  };

  return (
    <div
      ref={constraintsRef}
      className="fixed inset-0 pointer-events-none z-[10001]"
    >
      <motion.div
        ref={phoneRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        drag
        dragMomentum={false}
        dragConstraints={constraintsRef}
        // onDragEnd={handleDragEnd}
        // style={{ x: position.x, y: position.y }}
        className={phoneWrapperClasses}
        dragListener={false}
        // dragControls={dragControls}
      >
        <div className={phoneContentClasses}>
          <div className="flex flex-col items-center gap-4">
            <ContactInfo
              contacts={contacts}
              phoneNumber={currentNumber ?? ''}
              onOpenProfile={handleOpenProfile}
              loading={contactsLoading}
            />

            {phoneState === STATES.PHONE.ESTABLISHED && (
              <Typography className="text-sm text-gray-500">
                {formatDuration(duration)}
              </Typography>
            )}

            {phoneState === STATES.PHONE.INCOMING && (
              <Typography className="text-sm text-gray-500">
                {/* TODO: check when implementing i18n */}
                Llamada entrante...
              </Typography>
            )}
          </div>

          {renderCallControls()}
        </div>
      </motion.div>
    </div>
  );
};
